/**
 * Sparkline - Mini gráfico de línea para tarjetas de mercados
 * Muestra la tendencia basada en los datos históricos (5 días)
 * ESTILO TRADING: Colores que cambian según esté por encima o debajo del valor de apertura
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';

const Sparkline = ({ 
  data = [], 
  width = "100%", 
  height = "100%", 
  strokeWidth = 2.5,
  positive: externalPositive = null,
  color = null,
  className = '',
  isLoading = false,
  stale = false,  // Datos retrasados (último dato bueno por rate-limit de Yahoo) → parpadeo suave + punto ámbar
  baselinePrice = null,  // Precio de cierre de ayer (previousClose) para usar como referencia
  dayChangePercent = null,  // % de HOY: si se pasa, el cierre se deriva del último punto de la serie (mismo escalado) → el color SIEMPRE coincide con HOY
  currentPrice = null,  // Precio actual de la tarjeta: si se pasa, la serie TERMINA en este valor para que el color/punto final coincidan con el "HOY" (misma fuente)
  postFromRatio = null,  // Ratio (0-1) donde empieza el after-hours: esos segmentos se dibujan punteados
  showHoverValue = true,  // Al pasar el ratón muestra el valor del punto más cercano (tooltip)
  hoverSuffix = '',  // Sufijo para el valor del tooltip (ej. '€')
  showEndDot = true,  // Muestra el punto final + anillo de pulso "en vivo" (se oculta en tarjetas)
  showDayRange = true,  // Muestra marcas del máximo y mínimo de HOY dentro del sparkline
  showDaySummary = false,  // Al pasar el ratón muestra una mini leyenda con Apertura/Máx/Mín/Cierre del día
  times = null  // Timestamps (epoch ms) alineados con data → el tooltip también muestra la hora
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 100, height: 40 });
  const [hover, setHover] = useState(null);
  // Hover dedicado sobre las marcas máx/mín del día y la línea de cierre (jun 2026):
  // {type:'max'|'min'|'close', x, y, price}. Cuando está activo se oculta el
  // crosshair genérico para no solapar tooltips.
  const [markHover, setMarkHover] = useState(null);
  
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setDimensions({ width: rect.width, height: rect.height });
        }
      }
    };
    
    updateDimensions();
    const timer = setTimeout(updateDimensions, 50);
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);
  
  const actualWidth = dimensions.width;
  const actualHeight = dimensions.height;
  
  const gradientId = useMemo(() => `sparkline-grad-${Math.random().toString(36).substr(2, 9)}`, []);
  
  // Procesar datos y calcular segmentos basados en valor de referencia (previousClose o primer punto)
  const { segments, isPositiveTrend, lastPoint, openPrice, openY, showBaseline, points, maxPoint, minPoint, maxIdx, minIdx, dayOpen, dayHigh, dayLow, dayClose } = useMemo(() => {
    if (!data || data.length < 2 || actualWidth <= 0 || actualHeight <= 0) {
      return { segments: [], isPositiveTrend: true, lastPoint: null, openPrice: 0, openY: 0, showBaseline: false, points: [], maxPoint: null, minPoint: null };
    }
    
    // Extraer precios
    const prices = [];
    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      let price = null;
      if (typeof d === 'number') {
        price = d;
      } else if (d && typeof d === 'object') {
        price = d.close ?? d.price ?? d.value ?? null;
      }
      if (price !== null && !isNaN(price) && price > 0) {
        prices.push(price);
      }
    }
    
    if (prices.length < 2) {
      return { segments: [], isPositiveTrend: true, lastPoint: null, openPrice: 0, openY: 0, showBaseline: false, points: [], maxPoint: null, minPoint: null };
    }

    // Sincronizar el final de la serie con el precio actual de la tarjeta (misma
    // fuente que el "HOY"). Así el último punto y el color final coinciden con el
    // número HOY. PERO solo si el precio en vivo cae dentro de una banda razonable
    // de la serie: si ha "gapeado" fuera (dato del sparkline algo retrasado vs
    // precio en vivo, típico en PRE-mercado donde el rango intradía es minúsculo),
    // mantener el último punto REAL para no dibujar una recta + pico vertical.
    const rawMin = Math.min(...prices);
    const rawMax = Math.max(...prices);
    const rawRange = (rawMax - rawMin) || Math.abs(rawMax) * 0.001 || 1;
    const tol = rawRange * 0.05; // margen mínimo: solo tocar el borde de la serie
    if (currentPrice !== null && Number.isFinite(currentPrice) && currentPrice > 0) {
      if (currentPrice >= rawMin - tol && currentPrice <= rawMax + tol) {
        prices[prices.length - 1] = currentPrice;
      }
    }
    
    // Referencia (cierre de ayer):
    // PRIORIDAD (fix jun 2026 — color invertido vs "% HOY"): derivar la referencia
    // del % de HOY sobre el ÚLTIMO punto REAL de la serie →
    //   referencePrice = last / (1 + %HOY/100)
    // Así la referencia queda SIEMPRE en la MISMA moneda que la serie (raw/nativa)
    // y el COLOR del sparkline coincide SIEMPRE con el "% HOY" que muestra la
    // tarjeta. Antes se priorizaba baselinePrice, pero en acciones USA la serie
    // viene en USD y baselinePrice/currentPrice llegaban en EUR → color invertido
    // (ej.: BRK-B verde con HOY -0.09%; KO rojo con HOY +0.10%). El %HOY es
    // adimensional, por lo que este cálculo es inmune a la mezcla EUR/USD.
    // Fallbacks: baselinePrice si no hay %HOY; y por último el primer punto.
    let referencePrice;
    const lastReal = prices[prices.length - 1];
    if (dayChangePercent !== null && Number.isFinite(dayChangePercent) && dayChangePercent > -100) {
      referencePrice = lastReal / (1 + dayChangePercent / 100);
    } else if (baselinePrice && baselinePrice > 0) {
      referencePrice = baselinePrice;
    } else {
      referencePrice = prices[0];
    }
    
    // Calcular min/max de la serie de precios (con un pequeño margen) y ADEMÁS
    // incluir el cierre de ayer (referencePrice) en el dominio para que la línea
    // punteada de referencia SIEMPRE se vea. Para que un gap de apertura grande
    // no aplaste la serie intradía a una franja mínima, limitamos cuánto puede
    // extender la referencia el dominio (máx. ~1.5× el rango de la serie a cada
    // lado): en la gran mayoría de activos (gaps pequeños) la referencia se ve
    // completa; solo en gaps extremos queda clavada cerca del borde.
    const seriesMin = Math.min(...prices);
    const seriesMax = Math.max(...prices);
    const seriesRange = (seriesMax - seriesMin) || Math.abs(seriesMax) * 0.001 || 1;
    const pad = seriesRange * 0.08;
    // FIX PLANITUD (jun 2026): antes el eje se estiraba hasta 1,5× el rango de la serie
    // para incluir el "cierre de ayer". Si la referencia quedaba lejos (p.ej. por un dato
    // de precio corrupto o un gap), la serie real se APLASTABA hasta parecer una recta.
    // Ahora limitamos la extensión a 0,45× el rango de la serie: la serie SIEMPRE ocupa
    // ≥ ~65% de la altura (relieve garantizado). Si la referencia cae más lejos, se dibuja
    // pegada al borde (o se oculta) pero NUNCA aplana el gráfico intradía.
    const maxExtend = seriesRange * 0.45;
    const refLow = Math.max(referencePrice, seriesMin - maxExtend);
    const refHigh = Math.min(referencePrice, seriesMax + maxExtend);
    const min = Math.min(seriesMin - pad, refLow);
    const max = Math.max(seriesMax + pad, refHigh);
    const range = max - min || 1;

    // ¿La línea de referencia (cierre de ayer) cae dentro del rango visible?
    // Con el dominio ampliado se ve salvo en gaps extremos (que la dejarían fuera).
    const showBaseline = referencePrice >= min - 1e-9 && referencePrice <= max + 1e-9;
    
    // Calcular puntos
    const points = prices.map((price, i) => ({
      x: (i / (prices.length - 1)) * actualWidth,
      y: actualHeight - ((price - min) / range) * (actualHeight - 4) - 2,
      price
    }));

    // Índices del MÁXIMO y MÍNIMO de HOY (para las marcas de rango del día)
    let maxIdx = 0, minIdx = 0;
    for (let i = 1; i < prices.length; i++) {
      if (prices[i] > prices[maxIdx]) maxIdx = i;
      if (prices[i] < prices[minIdx]) minIdx = i;
    }
    
    // Posición Y del valor de referencia (para la línea de baseline)
    const referenceY = actualHeight - ((referencePrice - min) / range) * (actualHeight - 4) - 2;
    
    // Índice donde empieza el after-hours (para dibujarlo punteado)
    const postStartIndex = (postFromRatio !== null && Number.isFinite(postFromRatio) && postFromRatio > 0 && postFromRatio < 1)
      ? Math.round(postFromRatio * (prices.length - 1))
      : null;

    // Crear segmentos con colores basados en si están por encima o debajo del precio de referencia
    const segs = [];
    for (let i = 0; i < points.length - 1; i++) {
      const curr = points[i];
      const next = points[i + 1];
      const dashed = postStartIndex !== null && i >= postStartIndex;
      // El color se determina por la posición del punto respecto al precio de referencia (previousClose)
      // Si el precio está por encima de referencia = verde (subiendo vs ayer)
      // Si está por debajo = rojo (bajando vs ayer)
      const currAboveRef = curr.price >= referencePrice;
      const nextAboveRef = next.price >= referencePrice;
      
      // Si ambos puntos están del mismo lado, usar un solo color
      if (currAboveRef === nextAboveRef) {
        segs.push({
          x1: curr.x, y1: curr.y,
          x2: next.x, y2: next.y,
          color: currAboveRef ? '#00ff6a' : '#ff4444',
          dashed
        });
      } else {
        // Si cruzan la línea de referencia, dividir el segmento
        const ratio = (referencePrice - curr.price) / (next.price - curr.price);
        const intersectX = curr.x + ratio * (next.x - curr.x);
        const intersectY = referenceY;
        
        // Primer segmento (desde curr hasta intersección)
        segs.push({
          x1: curr.x, y1: curr.y,
          x2: intersectX, y2: intersectY,
          color: currAboveRef ? '#00ff6a' : '#ff4444',
          dashed
        });
        
        // Segundo segmento (desde intersección hasta next)
        segs.push({
          x1: intersectX, y1: intersectY,
          x2: next.x, y2: next.y,
          color: nextAboveRef ? '#00ff6a' : '#ff4444',
          dashed
        });
      }
    }
    
    // Tendencia general: último precio vs precio de referencia (previousClose)
    const lastPrice = prices[prices.length - 1];
    
    return {
      segments: segs,
      isPositiveTrend: lastPrice >= referencePrice,
      lastPoint: points[points.length - 1],
      openPrice: referencePrice,
      openY: referenceY,
      showBaseline,
      points,
      maxPoint: { ...points[maxIdx], price: prices[maxIdx] },
      minPoint: { ...points[minIdx], price: prices[minIdx] },
      maxIdx,
      minIdx,
      dayOpen: prices[0],
      dayHigh: prices[maxIdx],
      dayLow: prices[minIdx],
      dayClose: prices[prices.length - 1],
    };
  }, [data, actualWidth, actualHeight, baselinePrice, dayChangePercent, currentPrice, postFromRatio]);

  const positive = externalPositive !== null ? externalPositive : isPositiveTrend;
  
  // Skeleton
  if (!data || data.length < 2 || isLoading) {
    return (
      <div 
        ref={containerRef}
        className={`relative overflow-hidden rounded w-full h-full ${className}`}
      >
        <div 
          className="absolute inset-0 bg-gradient-to-r from-zinc-800/30 via-zinc-700/40 to-zinc-800/30 rounded animate-pulse"
        />
      </div>
    );
  }

  // Si no hay datos procesados
  if (segments.length === 0) {
    return (
      <div ref={containerRef} className={`w-full h-full ${className}`}>
        <div className="w-full h-full bg-zinc-800/20 rounded" />
      </div>
    );
  }

  const mainColor = positive ? '#00ff6a' : '#ff4444';

  const onHoverMove = (e) => {
    if (!showHoverValue || !containerRef.current || !points || points.length < 2) return;
    const rect = containerRef.current.getBoundingClientRect();
    const rx = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const idx = Math.round(rx * (points.length - 1));
    const p = points[idx];
    if (!p) return;
    let timeLabel = '';
    const ts = times && times[idx];
    if (ts) {
      timeLabel = new Date(ts).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid',
      });
    }
    setHover({ x: p.x, y: p.y, price: p.price, timeLabel, kind: idx === maxIdx ? 'max' : idx === minIdx ? 'min' : null });
  };

  const fmtHoverVal = (v) => {
    const abs = Math.abs(v);
    const dec = abs >= 1000 ? 2 : abs >= 1 ? 2 : 4;
    return new Intl.NumberFormat('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(v);
  };

  const tipLeft = hover ? Math.max(24, Math.min(hover.x, actualWidth - 24)) : 0;

  // "Resumen del día" (jun 2026): OHLC + recorrido intradía (Máx−Mín en € y %) +
  // variación del día (Cierre vs cierre de ayer). Se calcula una sola vez por render.
  const daySummary = (() => {
    if (!showDaySummary) return null;
    const rangeAbs = (Number.isFinite(dayHigh) && Number.isFinite(dayLow)) ? (dayHigh - dayLow) : null;
    const rangePct = (rangeAbs !== null && dayLow > 0) ? (rangeAbs / dayLow) * 100 : null;
    let changePct = null;
    if (dayChangePercent !== null && Number.isFinite(dayChangePercent)) changePct = dayChangePercent;
    else if (Number.isFinite(openPrice) && openPrice > 0 && Number.isFinite(dayClose)) changePct = (dayClose / openPrice - 1) * 100;
    return { rangeAbs, rangePct, changePct };
  })();
  const fmtPct = (v) => `${v >= 0 ? '+' : '−'}${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(v))}%`;

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full ${className}`}
      onMouseMove={showHoverValue ? onHoverMove : undefined}
      onMouseLeave={showHoverValue ? () => setHover(null) : undefined}
    >
      {/* Indicador de datos retrasados: punto ámbar discreto + tooltip */}
      {stale && (
        <span
          className="absolute top-0 right-0 z-10 w-1.5 h-1.5 rounded-full bg-gold-400"
          style={{ boxShadow: '0 0 6px rgba(245,158,11,0.9)' }}
          title="Datos retrasados: Yahoo saturado, mostrando la última tendencia disponible"
          data-testid="sparkline-stale-dot"
        />
      )}
      <svg 
        width="100%" 
        height="100%" 
        viewBox={`0 0 ${actualWidth} ${actualHeight}`}
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
        className={stale ? 'sparkline-stale' : ''}
      >
        {/* Línea de referencia en el cierre de ayer - solo si cae dentro del rango visible */}
        {showBaseline && openY > 0 && (
          <line
            x1={0}
            y1={openY}
            x2={actualWidth}
            y2={openY}
            stroke="#38e5ff"
            strokeWidth={1.5}
            strokeOpacity={0.9}
            strokeDasharray="4,3"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* Marcas del MÁXIMO y MÍNIMO de HOY: pequeños triángulos discretos.
            Se ocultan si coinciden con el punto final (para no solaparse con el
            punto "en vivo") o en sparklines muy estrechos. */}
        {showDayRange && actualWidth > 70 && maxPoint && lastPoint && Math.abs(maxPoint.x - lastPoint.x) > 6 && (
          <path
            d={`M ${maxPoint.x - 3} ${maxPoint.y - 3} L ${maxPoint.x + 3} ${maxPoint.y - 3} L ${maxPoint.x} ${maxPoint.y + 1} Z`}
            fill="#00ff6a"
            fillOpacity={0.85}
            data-testid="sparkline-day-high"
          />
        )}
        {showDayRange && actualWidth > 70 && minPoint && lastPoint && Math.abs(minPoint.x - lastPoint.x) > 6 && (
          <path
            d={`M ${minPoint.x - 3} ${minPoint.y + 3} L ${minPoint.x + 3} ${minPoint.y + 3} L ${minPoint.x} ${minPoint.y - 1} Z`}
            fill="#ff5555"
            fillOpacity={0.85}
            data-testid="sparkline-day-low"
          />
        )}
        
        {/* Líneas de cada segmento con color según posición vs apertura.
            El after-hours (seg.dashed) se dibuja PUNTEADO. */}
        {segments.map((seg, i) => (
          <line
            key={`line-${i}`}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            strokeDasharray={seg.dashed ? '2.5,2.5' : undefined}
            strokeOpacity={seg.dashed ? 0.9 : 1}
          />
        ))}
        
        {/* Punto final estático. El anillo de pulso se dibuja como overlay HTML/CSS
            (abajo) para que la animación corra en el hilo compositor y NO repinte
            el sparkline ni la tarjeta en cada frame → scroll fluido en producción. */}
        {showEndDot && lastPoint && (
          <circle
            cx={lastPoint.x}
            cy={lastPoint.y}
            r="3"
            fill={mainColor}
          />
        )}

        {/* Zona de hover invisible sobre la LÍNEA DE CIERRE (jun 2026): una banda
            fina sobre la línea punteada para mostrar el precio de cierre al pasar
            el ratón. Las marcas máx/mín se detectan en el propio crosshair (abajo),
            así el valor se muestra en TODA la línea, hasta el final. */}
        {showHoverValue && showBaseline && openY > 0 && (
          <line
            x1={0}
            y1={openY}
            x2={actualWidth}
            y2={openY}
            stroke="transparent"
            strokeWidth={7}
            style={{ cursor: 'pointer' }}
            vectorEffect="non-scaling-stroke"
            onMouseEnter={() => setMarkHover({ type: 'close', price: openPrice })}
            onMouseLeave={() => setMarkHover(null)}
            data-testid="sparkline-close-hit"
          />
        )}
      </svg>

      {/* Anillo de pulso "en vivo" — animación CSS (compositor), sin repintar la tarjeta */}
      {showEndDot && lastPoint && actualWidth > 0 && (
        <span
          className="sparkline-pulse-ring"
          style={{ left: `${lastPoint.x}px`, top: `${lastPoint.y}px`, color: mainColor }}
          data-testid="sparkline-live-dot"
        />
      )}

      {/* Tooltip al pasar el ratón sobre la LÍNEA DE CIERRE (jun 2026): muestra el
          precio de cierre de ayer al posar el ratón sobre la línea punteada cian. */}
      {showHoverValue && markHover && markHover.type === 'close' && showBaseline && openY > 0 && actualWidth > 0 && (
        <div
          className="absolute z-50 px-1.5 py-0.5 rounded bg-cyan-950/95 border border-cyan-400/60 shadow-lg pointer-events-none whitespace-nowrap"
          style={{ left: '2px', top: `${openY}px`, transform: 'translateY(-50%)' }}
          data-testid="sparkline-close-tooltip"
        >
          <span className="text-[9px] font-mono font-bold text-cyan-300 tabular-nums">
            Cierre {fmtHoverVal(openPrice)}{hoverSuffix}
          </span>
        </div>
      )}

      {/* Tooltip al pasar el ratón: valor (y hora si hay timestamps) del punto más
          cercano. Si el punto es el MÁXIMO o MÍNIMO del día, se resalta en verde/rojo
          con la etiqueta "Máx"/"Mín" (petición usuario jun 2026). Funciona en TODA la
          línea, hasta el punto final. */}
      {showHoverValue && hover && actualWidth > 0 && (
        <>
          <span
            className="absolute top-0 bottom-0 w-px bg-white/25 pointer-events-none"
            style={{ left: `${hover.x}px` }}
          />
          <span
            className="absolute w-2 h-2 rounded-full bg-white pointer-events-none ring-1 ring-black/40"
            style={{ left: `${hover.x}px`, top: `${hover.y}px`, transform: 'translate(-50%, -50%)' }}
          />
          {/* La burbuja de valor por-punto se oculta si mostramos el panel "Resumen
              del día" (evita solapamiento); el valor del punto ya aparece dentro del panel. */}
          {!showDaySummary && (
            <div
              className={`absolute z-50 -top-1 px-1.5 py-0.5 rounded border shadow-lg pointer-events-none whitespace-nowrap ${hover.kind === 'max' ? 'bg-emerald-950/95 border-emerald-400/60' : hover.kind === 'min' ? 'bg-red-950/95 border-red-400/60' : 'bg-zinc-950/95 border-white/15'}`}
              style={{ left: `${tipLeft}px`, transform: 'translateX(-50%)' }}
              data-testid="sparkline-hover-tooltip"
            >
              <span
                className="text-[10px] font-bold font-mono tabular-nums"
                style={{ color: hover.kind === 'max' ? '#34d399' : hover.kind === 'min' ? '#f87171' : mainColor }}
              >
                {hover.kind === 'max' ? 'Máx ' : hover.kind === 'min' ? 'Mín ' : ''}{fmtHoverVal(hover.price)}{hoverSuffix}
              </span>
              {hover.timeLabel && <span className="ml-1 text-[9px] text-zinc-400 font-mono">{hover.timeLabel}</span>}
            </div>
          )}
        </>
      )}

      {/* Mini leyenda "Resumen del día" (jun 2026): al pasar el ratón muestra, de un
          vistazo, la Apertura, Máx, Mín y Cierre del día, la variación del día, el
          recorrido intradía (Máx−Mín) y el punto/hora bajo el cursor. Diseño premium,
          alineado y legible. Solo cuando el consumidor lo pide (showDaySummary). */}
      {showDaySummary && showHoverValue && (hover || markHover) && actualWidth > 0 && Number.isFinite(dayOpen) && (
        <div
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none"
          data-testid="sparkline-day-summary"
        >
          <div className="min-w-[168px] rounded-lg border border-gold-500/50 bg-[#0a0e16]/98 backdrop-blur-sm px-3 py-2 shadow-[0_10px_34px_rgba(0,0,0,0.9)] ring-1 ring-black/50">
            <div className="flex items-center justify-between gap-3 mb-1.5 pb-1.5 border-b border-white/10">
              <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-gold-300">Resumen del día</span>
              {daySummary && Number.isFinite(daySummary.changePct) && (
                <span
                  className={`text-[9px] font-mono font-bold tabular-nums px-1.5 py-0.5 rounded ${daySummary.changePct >= 0 ? 'text-emerald-300 bg-emerald-500/12' : 'text-red-300 bg-red-500/12'}`}
                  data-testid="sparkline-day-change"
                >
                  {fmtPct(daySummary.changePct)}
                </span>
              )}
            </div>
            <div className="space-y-[3px] text-[10px] font-mono tabular-nums leading-none">
              <div className="flex items-center justify-between gap-4">
                <span className="text-zinc-400">Apertura</span>
                <span className="text-zinc-100 font-semibold">{fmtHoverVal(dayOpen)}{hoverSuffix}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-emerald-400">Máximo</span>
                <span className="text-emerald-300 font-semibold">{fmtHoverVal(dayHigh)}{hoverSuffix}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-red-400">Mínimo</span>
                <span className="text-red-300 font-semibold">{fmtHoverVal(dayLow)}{hoverSuffix}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-zinc-400">Cierre</span>
                <span className="font-semibold" style={{ color: mainColor }}>{fmtHoverVal(dayClose)}{hoverSuffix}</span>
              </div>
            </div>
            {daySummary && daySummary.rangeAbs !== null && (
              <div className="mt-1.5 pt-1.5 border-t border-white/10 flex items-center justify-between gap-4 text-[10px] font-mono tabular-nums leading-none">
                <span className="text-gold-300/90 font-medium">Rango</span>
                <span className="text-zinc-200 font-semibold" data-testid="sparkline-day-range">
                  {fmtHoverVal(daySummary.rangeAbs)}{hoverSuffix}
                  {daySummary.rangePct !== null && <span className="text-zinc-500 ml-1.5">({new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(daySummary.rangePct)}%)</span>}
                </span>
              </div>
            )}
            {hover && (
              <div className="mt-1.5 pt-1.5 border-t border-white/10 flex items-center justify-between gap-4 text-[10px] font-mono tabular-nums leading-none">
                <span className={`font-medium ${hover.kind === 'max' ? 'text-emerald-400' : hover.kind === 'min' ? 'text-red-400' : 'text-zinc-400'}`}>
                  {hover.kind === 'max' ? 'Máximo' : hover.kind === 'min' ? 'Mínimo' : 'Punto'}
                </span>
                <span className="font-semibold" style={{ color: hover.kind === 'max' ? '#34d399' : hover.kind === 'min' ? '#f87171' : '#e5e7eb' }}>
                  {fmtHoverVal(hover.price)}{hoverSuffix}{hover.timeLabel ? ` · ${hover.timeLabel}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Sparkline);
